import { SelfServiceUserAccount } from '@ekt-group/general-purpose-api-interfaces';
import { CustomerContractRole } from '@ekt-group/general-purpose-api-interfaces/src/types/customer-contract-role.enum';
import { Building05, User01 } from '@untitled-ui/icons-react';
import { ComponentPropsWithoutRef, forwardRef } from 'react';

import Button from '@/components/ui/Button';
import { LogoutButton, useUser, UserAccountList } from '@/features/auth';
import { useUserInfoStore } from '@/store';
import { cn } from '@/utils/cn';

interface UserDropdownProps extends ComponentPropsWithoutRef<'div'> {
  isDropdownVisible?: boolean;
  onUserButtonClick?: () => void;
  onAccountSelect?: (account: SelfServiceUserAccount) => void;

  userListClassName?: string;
}

export const UserDropdown = forwardRef<HTMLDivElement, UserDropdownProps>(
  ({ className, onUserButtonClick, isDropdownVisible = true, onAccountSelect, userListClassName }, ref) => {
    const { data: user } = useUser();
    const { name: accountName, role } = useUserInfoStore((state) => state.selectedAccount);

    const icon =
      role === CustomerContractRole.ContractOwner || role === CustomerContractRole.AuthorisedPerson ? <User01 /> : <Building05 />;

    return (
      <div ref={ref} className={cn('inline-block relative', className)}>
        <Button onClick={onUserButtonClick} className="sticky flex flex-shrink-0 items-center w-[11rem]">
          {icon}
          <div className="flex-1 overflow-hidden text-base text-ellipsis whitespace-nowrap" style={{ minWidth: 0 }}>
            {accountName || user?.fullName}
          </div>
        </Button>

        <div
          className={cn(
            'animate-fade-down animate-duration-300 top-10 right-0 z-10 absolute justify-between flex flex-col gap-4 bg-white shadow-primary mt-3 px-5 py-6 rounded-[38px] w-[300px] h-[450px] origin-top-right',
            'before:content-[""] before:absolute before:-top-2 before:right-20 before:border-l-transparent before:border-r-transparent before:border-b-white before:border-l-[10px] before:border-r-[10px] before:border-b-[10px]',
            { hidden: !isDropdownVisible },
          )}
        >
          <UserAccountList
            accounts={user?.accounts}
            onAccountSelect={onAccountSelect}
            shouldToggleSearchBar
            className={userListClassName}
          />
          <div className="flex flex-col gap-2">
            <LogoutButton />
          </div>
        </div>
      </div>
    );
  },
);

UserDropdown.displayName = 'UserDropdown';
